import React, { useState } from 'react';
import { X, Lock, Eye, EyeOff, ShieldAlert } from 'lucide-react';
import { safeFetch, extractError } from '../lib/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ChangePasswordModal — reusable in-page modal that calls
 * POST /api/auth/change-password. Designed to be opened from anywhere
 * once the user is authenticated (token is in localStorage).
 *
 * Props:
 *   open      : boolean
 *   onClose   : () => void          — fires on cancel / backdrop / X button
 *   onSuccess : () => void          — fires after a successful change
 *   forced    : boolean             — when true, hides Cancel + backdrop close
 *                                     (use for expired-password forced flow)
 *   title     : string (optional)
 *   subtitle  : string (optional)
 */
export default function ChangePasswordModal({
  open,
  onClose,
  onSuccess,
  forced = false,
  title = 'Change Password',
  subtitle = 'Use a strong password you don’t reuse elsewhere.',
}) {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const reset = () => {
    setOldPwd(''); setNewPwd(''); setConfirmPwd('');
    setShowOld(false); setShowNew(false); setError('');
  };

  const handleClose = () => {
    if (busy) return;
    reset();
    onClose?.();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPwd.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (newPwd !== confirmPwd) {
      setError('New password and confirmation don’t match.');
      return;
    }
    if (newPwd === oldPwd) {
      setError('New password must be different from the old one.');
      return;
    }

    setBusy(true);
    const token = localStorage.getItem('token');
    const result = await safeFetch(`${API_URL}/api/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ old_password: oldPwd, new_password: newPwd }),
    });
    setBusy(false);

    if (result.ok) {
      reset();
      onSuccess?.();
    } else {
      setError(extractError(result, 'Could not change password'));
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={() => { if (!forced) handleClose(); }}
      data-testid="change-password-backdrop"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="change-password-modal"
      >
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div className="flex items-start gap-3">
            <div className={`p-2 rounded-lg ${forced ? 'bg-amber-100' : 'bg-blue-100'}`}>
              {forced
                ? <ShieldAlert size={20} className="text-amber-600" />
                : <Lock size={20} className="text-blue-600" />}
            </div>
            <div>
              <h3 className="font-semibold text-gray-900" data-testid="cp-title">{title}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
            </div>
          </div>
          {!forced && (
            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 p-1"
              data-testid="cp-close-btn"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>

        <form onSubmit={submit} className="px-5 py-4 space-y-3">
          <PwdField
            label="Current password"
            value={oldPwd}
            onChange={setOldPwd}
            show={showOld}
            onToggle={() => setShowOld(s => !s)}
            testId="cp-old"
            autoComplete="current-password"
          />
          <PwdField
            label="New password (min 6 chars)"
            value={newPwd}
            onChange={setNewPwd}
            show={showNew}
            onToggle={() => setShowNew(s => !s)}
            testId="cp-new"
            autoComplete="new-password"
          />
          <PwdField
            label="Confirm new password"
            value={confirmPwd}
            onChange={setConfirmPwd}
            show={showNew}
            onToggle={() => setShowNew(s => !s)}
            testId="cp-confirm"
            autoComplete="new-password"
          />

          {error && (
            <div className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="cp-error">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            {!forced && (
              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="px-4 py-2 text-sm rounded-lg text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                data-testid="cp-cancel-btn"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              disabled={busy || !oldPwd || !newPwd || !confirmPwd}
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
              data-testid="cp-submit-btn"
            >
              {busy ? 'Saving…' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PwdField({ label, value, onChange, show, onToggle, testId, autoComplete }) {
  return (
    <label className="block">
      <span className="block text-xs text-gray-600 mb-1">{label}</span>
      <div className="relative">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete={autoComplete}
          className="w-full px-3 py-2 pr-10 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid={`${testId}-input`}
          required
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
          aria-label={show ? 'Hide password' : 'Show password'}
          data-testid={`${testId}-toggle`}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </label>
  );
}
